import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { AdminPanelPage } from '../../AdminPanelPage';
import { ApplicantGroupForm, createTitleEditor, createDescriptionEditor } from './ApplicantGroupForm';
import { usePost } from '../../../API';
import { FilterGroupToBooleanExprConverter, BooleanExprToFilterGroupConverter } from '../../shared/utils/filterConverters';
import { BooleanExpr } from '@aidkitorg/types/lib/survey';
import { ApplicantGroupFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';

export const EditApplicantGroupPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<{
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  } | null>(null);
  const [error, setError] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const fetchApplicantGroup = usePost('/api/admin_panel/applicant_group');
  const updateApplicantGroupApi = usePost('/api/admin_panel/applicant_groups/update');

  const getCurrentValues = () => {
    const nameInput = nameRef.current?.value || '';
    const descriptionInput = descriptionRef.current?.value || '';

    return {
      name: nameInput,
      description: descriptionInput
    };
  };

  // Fetch group data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(false);
        const response = await fetchApplicantGroup({ id });

        if (response?.success && response.data) {
          // Convert filters from BooleanExpr to ApplicantGroupFilterConfig if needed
          let filters = response.data.filters;

          // Check if filters.config is a BooleanExpr (has 'kind' property) instead of FilterGroup
          if (filters && filters.config && 'kind' in filters.config) {
            // Convert BooleanExpr to FilterGroup format
            const filterGroup = BooleanExprToFilterGroupConverter.convert(filters.config as BooleanExpr);
            if (filterGroup) {
              filters = { enabled: true, config: filterGroup };
            }
          }

          setInitialValues({
            name: response.data.name,
            description: response.data.description || '',
            filters
          });
        } else {
          throw new Error('Failed to fetch applicant group details');
        }
      } catch (err) {
        console.error('Error fetching applicant group:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const updateApplicantGroup = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }): Promise<void> => {
    // Convert the filter config to BooleanExpr format
    const filterBooleanExpr: BooleanExpr | undefined = FilterGroupToBooleanExprConverter.convertFromConfig(data.filters);

    // Validate that we have a valid filter expression
    if (!filterBooleanExpr) {
      throw new Error('Failed to convert filters to valid expression');
    }

    // Call the API to update the applicant group
    const response = await updateApplicantGroupApi({
      id,
      name: data.name,
      description: data.description || null,
      filter: filterBooleanExpr
    });

    if (!response || !response.success) {
      throw new Error('Failed to update applicant group: Invalid response from server');
    }
  };

  const handleSubmit = async (data: {
    name: string;
    description: string;
    filters: ApplicantGroupFilterConfig;
  }) => {
    setIsSubmitting(true);

    try {
      await updateApplicantGroup(data);
      history.push(`/admin-panel/applicants/applicant-groups/${id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      history.push(`/admin-panel/applicants/applicant-groups/${id}`);
    }
  };

  const titleEditor = createTitleEditor(
    nameRef,
    strings,
    initialValues?.name || '',
    showValidation,
    getCurrentValues
  );
  const descriptionEditor = createDescriptionEditor(descriptionRef, strings, initialValues?.description || '');

  const pageActionButtons = [
    <Button
      key="cancel"
      type="button"
      variant="secondary"
      disabled={isSubmitting}
      onClick={handleCancel}
    >
      {strings.cancel}
    </Button>,
    <Button
      key="save"
      type="submit"
      variant="primary"
      disabled={isSubmitting}
      loading={isSubmitting}
      form="applicant-group-form"
    >
      {strings.save_changes}
    </Button>
  ];

  if (isLoading) {
    return (
      <AdminPanelPage
        title={strings.edit_group}
        breadcrumbs={[
          { label: L.admin_panel.nav.applicants, href: '/admin-panel/applicants' },
          { label: strings.title, href: '/admin-panel/applicants/applicant-groups' },
          { label: strings.edit_group }
        ]}
      >
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-600">{strings.loading || 'Loading...'}</div>
        </div>
      </AdminPanelPage>
    );
  }

  if (error || !initialValues) {
    return (
      <AdminPanelPage
        title={strings.edit_group}
        breadcrumbs={[
          { label: L.admin_panel.nav.applicants, href: '/admin-panel/applicants' },
          { label: strings.title, href: '/admin-panel/applicants/applicant-groups' },
          { label: strings.edit_group }
        ]}
      >
        <div className="flex justify-center items-center py-12">
          <div className="text-red-600">{strings.error_loading || 'Error loading applicant group'}</div>
        </div>
      </AdminPanelPage>
    );
  }

  return (
    <AdminPanelPage
      title={titleEditor}
      description={descriptionEditor}
      breadcrumbs={[
        { label: L.admin_panel.nav.applicants, href: '/admin-panel/applicants' },
        { label: strings.title, href: '/admin-panel/applicants/applicant-groups' },
        { label: initialValues.name }
      ]}
      pageActionButtons={pageActionButtons}
    >
      <ApplicantGroupForm
        mode="edit"
        groupId={id}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        nameRef={nameRef}
        descriptionRef={descriptionRef}
        showValidation={showValidation}
        setShowValidation={setShowValidation}
      />
    </AdminPanelPage>
  );
};
